import { Link } from "react-router-dom";
import { useUndervaluedGems } from "@/hooks/useUndervaluedGems";
import { UndervaluedGemCard } from "@/components/UndervaluedGemCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Gem, ArrowRight, Clock } from "lucide-react";

const formatAgo = (iso?: string) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const UndervaluedGemsWidget = () => {
  const { data, isLoading } = useUndervaluedGems(3);

  return (
    <section className="container mx-auto px-4 my-12">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 mb-2">
            <Gem className="w-5 h-5 text-emerald-400" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400">
              Fundamental Value
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-300 via-cyan-300 to-primary bg-clip-text text-transparent">
            Undervalued Gems on Swyftx
          </h2>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Tokens trading below fair value based on revenue, TVL, and on-chain growth — not momentum.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data?.[0]?.last_updated && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              Updated {formatAgo(data[0].last_updated)}
            </div>
          )}
          <Button asChild variant="outline">
            <Link to="/undervalued-gems">
              See Top 8 <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[460px] rounded-lg" />)}
        </div>
      ) : !data || data.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
          No gems yet. An admin can run "Refresh Gems Now" to populate this section.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {data.map((g) => <UndervaluedGemCard key={g.id} gem={g} hero />)}
        </div>
      )}
    </section>
  );
};
